// The tech app's design (DEC-018, Amir 26 Oct): Blueprint on the iPhone, with one accent. It is the
// Blueprint set (blueprint.ts) with the tech app's own layers on top, so the drawing, the type, the
// steps and the rules stay the office's and only what the iPhone adds is new:
// - base: the "Fresh water" palette Amir picked (water.600 / water.300 and their washes), the price book
//   picture's night ground (the art worker's ART_RECOLOURS.dark), and the iPhone's sizes;
// - meaning: what the accent is for (acc-fill, acc-quiet, acc-line …), the letterhead's rule;
// - component: what each of the tech kit's components reads (apps/tech/src/design), prefixed t-.
// The office's own component tokens stay in blueprint.ts and aren't carried here.
//
// The rulings it holds (apps/tech/DESIGN.md):
// - the accent is the one filled thing on a screen, the step and tab you're on, a price; red stays the
//   problem's, and status stays drawn (done hatched, waiting dashed);
// - the company's own colour (bp-brand, its branding.primaryColor on a customer theme) is drawn only by
//   the letterhead's rule and the company's mark: no other token here may name it (test/tech.test.ts).

import type { Brand, TokenSet } from '../schema.ts';
import { blueprint } from './blueprint.ts';

/** The made-up company the tech app's stories draw for (Harbour Plumbing's own colour, a sea teal). */
export const TECH_STORY_BRAND: Brand = { primary: '#0B6E69' };

/** The accent, light and dark: Fresh water (Amir, 26 Oct). */
export const FRESH_WATER = {
  light: { acc: '#0A68B0', onAcc: '#FFFFFF', wash: '#E2EEF8' },
  dark: { acc: '#6DB5EF', onAcc: '#0A1826', wash: '#12304A' },
} as const;

const b = blueprint;

/** The tech kit's component tokens: what each component in apps/tech/src/design reads, and nothing else. */
export const TECH_COMPONENTS = {
  // ── Primitives ──
  // Text: the type roles, each a size on the scale, a leading and a weight
  't-text-ink': '{color.bp-ink}',
  't-text-muted': '{color.bp-muted}',
  't-text-acc': '{acc-fill}',
  't-text-bad': '{color.tc-bad}',
  't-text-on-acc': '{acc-on}',
  't-text-caption-fs': '{fs.caption}',
  't-text-caption-lh': '{lh.snug}',
  't-text-caption-fw': '{fw.body}',
  't-text-label-fs': '{fs.label}',
  't-text-label-lh': '{lh.list}',
  't-text-label-fw': '{fw.strong}',
  't-text-sm-fs': '{fs.body-sm}',
  't-text-sm-lh': '{lh.list}',
  't-text-sm-fw': '{fw.body}',
  't-text-table-fs': '{fs.table}',
  't-text-table-lh': '{lh.list}',
  't-text-table-fw': '{fw.body}',
  't-text-body-fs': '{fs.body}',
  't-text-body-lh': '{lh.body}',
  't-text-body-fw': '{fw.body}',
  't-text-lede-fs': '{fs.body-lg}',
  't-text-lede-lh': '{lh.list}',
  't-text-lede-fw': '{fw.body}',
  't-text-name-fs': '{fs.body-lg}',
  't-text-name-lh': '{lh.snug}',
  't-text-name-fw': '{fw.strong}',
  't-text-title-fs': '{fs.name}',
  't-text-title-lh': '{lh.snug}',
  't-text-title-fw': '{fw.strong}',
  't-text-h3-fs': '{fs.h3}',
  't-text-h3-lh': '{lh.tight}',
  't-text-h3-fw': '{fw.strong}',
  't-text-h2-fs': '{fs.h2}',
  't-text-h2-lh': '{lh.tight}',
  't-text-h2-fw': '{fw.strong}',
  't-text-figure-fs': '{fs.figure}',
  't-text-figure-lh': '{lh.tight}',
  't-text-figure-fw': '{fw.figure}',
  't-text-total-fs': '{fs.figure-lg}',
  't-text-total-lh': '{lh.tight}',
  't-text-total-fw': '{fw.total}',
  't-text-strong-fw': '{fw.strong}',
  // Icon: a 22 px glyph drawn at the icon stroke; the small one (a row's chevron, a tick)
  't-icon-size': '{size.glyph}',
  't-icon-size-sm': '{size.glyph-sm}',
  't-icon-size-xs': '{size.tick}',
  't-icon-stroke': '{icon-stroke}',
  't-icon-stroke-bold': '{w.focus}',
  't-icon-ink': '{color.bp-ink}',
  't-icon-muted': '{color.bp-muted}',
  // Button: 44 tall (52 at the thumb), boxed; the one primary filled in the accent; waiting dashed
  't-btn-h': '{control-h-touch}',
  't-btn-h-big': '{control-h-big}',
  't-btn-pad-x': '{space.6}',
  't-btn-gap': '{space.3}',
  't-btn-fs': '{fs.body}',
  't-btn-fs-big': '{fs.body-lg}',
  't-btn-fw': '{fw.strong}',
  't-btn-lh': '{lh.snug}',
  't-btn-border': '{line-rule}',
  't-btn-radius': '{radius.r-hair}',
  't-btn-bg': '{color.bp-surface}',
  't-btn-ink': '{color.bp-ink}',
  't-btn-pri-bg': '{acc-fill}',
  't-btn-pri-ink': '{acc-on}',
  't-btn-wait-border': '{dash}',
  't-btn-disabled-border': '{hair}',
  't-btn-disabled-ink': '{color.bp-muted}',
  't-btn-glyph': '{size.glyph}',
  't-btn-focus': '{focus-line}',
  // IconButton: a glyph alone, 52 wide (the head's call and directions); 52 tall in the dock; a 44
  // square beside a question and on a sheet's close
  't-iconbtn-w': '{control-h-big}',
  't-iconbtn-h': '{control-h-touch}',
  't-iconbtn-h-big': '{control-h-big}',
  't-iconbtn-sm': '{control-h-touch}',
  't-iconbtn-border': '{line-rule}',
  't-iconbtn-border-quiet': '{hair}',
  't-iconbtn-radius': '{radius.r-hair}',
  't-iconbtn-bg': '{color.bp-surface}',
  't-iconbtn-ink': '{color.bp-ink}',
  't-iconbtn-fs': '{fs.body}',
  't-iconbtn-fw': '{fw.strong}',
  // Link: an underlined word, a 44 px target
  't-link-h': '{control-h-touch}',
  't-link-fs': '{fs.body}',
  't-link-fs-sm': '{fs.body-sm}',
  't-link-fw': '{fw.body}',
  't-link-ink': '{color.bp-ink}',
  't-link-gap': '{space.6}',
  // StateMark: the drawn state of a stop, a step, a shot or a way to pay (DESIGN.md rule 4)
  't-mark-size': '{size.node}',
  't-mark-radius': '{radius.r-round}',
  't-mark-line': '{line-rule}',
  't-mark-bg': '{color.bp-surface}',
  't-mark-now': '{acc-fill}',
  't-mark-done-line': '{w.rule} solid {color.bp-muted}',
  't-mark-wait-line': '{dash}',
  't-mark-bad-line': '{line-signal}',
  // Hatch: done is hatched in ink; a done step, an empty photo in the paper's grid
  't-hatch-ink': '{color.bp-ink}',
  't-hatch-w': '{w.rule}',
  't-hatch-gap': 5,
  't-hatch-muted-ink': '{color.bp-grid}',
  't-hatch-muted-bg': '{color.bp-surface}',
  't-hatch-muted-w': '{w.icon}',
  't-hatch-muted-gap': 6,
  // Segmented: one frame, hairlines between, the one that's on filled in ink (never the accent)
  't-seg-h': '{control-h-touch}',
  't-seg-border': '{line-rule}',
  't-seg-divider': '{hair}',
  't-seg-radius': '{radius.r-hair}',
  't-seg-bg': '{color.bp-surface}',
  't-seg-ink': '{color.bp-ink}',
  't-seg-on-bg': '{color.bp-ink}',
  't-seg-on-ink': '{color.bp-on-ink}',
  't-seg-mark': '{acc-mark}',
  't-seg-pad-x': '{space.2}',
  't-seg-fs': '{fs.body}',
  't-seg-fs-sm': '{fs.body-sm}',
  't-seg-fw-on': '{fw.strong}',
  // Checkbox: drawn, the accent when ticked, in a 44 px target
  't-check-size': '{size.check}',
  't-check-tap': '{control-h-touch}',
  't-check-border': '{line-rule}',
  't-check-radius': '{radius.r-hair}',
  't-check-bg': '{color.bp-surface}',
  't-check-on-bg': '{acc-fill}',
  't-check-on-ink': '{acc-on}',
  // Field: the one boxed thing besides a button; a find field is a button that opens the search
  't-field-h': '{control-h-touch}',
  't-field-border': '{line-rule}',
  't-field-radius': '{radius.r-hair}',
  't-field-bg': '{color.bp-surface}',
  't-field-ink': '{color.bp-ink}',
  't-field-muted': '{color.bp-muted}',
  't-field-pad-x': '{space.4}',
  't-field-pad-y': '{space.3}',
  't-field-gap': '{space.3}',
  't-field-multi-h': 96,
  't-field-fs': '{fs.body-lg}',
  // with − and + beside it (how many): the amount large in the middle, its unit after the +
  't-field-step-w': '{size.qty-field}',
  't-field-step-fs': '{fs.h2}',
  't-field-step-fw': '{fw.figure}',
  't-field-unit-gap': '{space.4}',
  // a value that can't be used: the box in the problem's red, the reason under it
  't-field-bad-border': '{w.rule} solid {color.tc-bad}',
  't-field-reason-gap': '{space.2}',
  // Picture: a price book picture on its own ground; an empty frame where it has none yet
  't-pic-ground': '{art-ground}',
  't-pic-radius': '{radius.r-hair}',
  't-pic-empty': '{dash}',
  't-pic-empty-bg': '{color.bp-surface}',
  't-pic-line': '{size.art-line}',
  't-pic-head': '{size.art-head}',
  't-pic-thumb': '{size.art-thumb}',
  // Figure: a label over a number
  't-fig-gap': '{space.1}',
  't-fig-ink': '{color.bp-ink}',
  't-fig-acc': '{acc-fill}',
  // PageDots: where you are in a swipe
  't-dots-size': '{size.dot}',
  't-dots-radius': '{radius.r-round}',
  't-dots-line': '{line-rule}',
  't-dots-on': '{acc-fill}',
  't-dots-gap': '{space.3}',
  't-dots-tap': '{control-h-touch}',
  // Meter: how far (6 of 24 counted): the ink's outline, filled in the accent as far as it's got
  't-meter-w': '{size.meter}',
  't-meter-h': '{space.3}',
  't-meter-line': '{line-rule}',
  't-meter-radius': '{radius.r-hair}',
  't-meter-bg': '{color.bp-surface}',
  't-meter-fill': '{acc-fill}',
  't-meter-gap': '{space.3}',
  // ── Components ──
  // RecordRow and RecordList: rows on hairlines, a 44 px row, the chevron when it opens something
  't-row-h': '{control-h-touch}',
  't-row-pad-y': '{pad-row}',
  't-row-gap': '{space.4}',
  't-row-sub-gap': '{space.1}',
  't-row-divider': '{hair}',
  't-row-chev': '{color.bp-muted}',
  't-row-qty-w': '{size.qty}',
  // the row whose detail is open, the rail's place you're in: on the accent's wash
  't-row-sel-bg': '{acc-quiet}',
  't-row-sel-pad-x': '{space.4}',
  // a second action at the row's end ("All 14"), beside the row's own press
  't-row-aside-gap': '{space.4}',
  // KeyValue: a fact on the left, its value on the right
  't-kv-gap-y': '{space.2}',
  't-kv-gap-x': '{space.6}',
  // Notice: a mark down its side, ink for a fact, red for a problem
  't-notice-mark': '{mark}',
  't-notice-mark-bad': '{w.mark} solid {color.tc-bad}',
  // waiting (kept with no signal, the office asked): the same mark, dashed (DESIGN.md rule 4)
  't-notice-mark-wait': '{w.mark} dashed {color.bp-ink}',
  't-notice-pad-x': '{space.5}',
  't-notice-pad-y': '{space.1}',
  't-notice-gap': '{space.1}',
  // WaitArea: the waiting drawing at an area's size (DESIGN.md rule 4), dashed on the paper's grid
  't-wait-line': '{wait-area}',
  't-wait-bg': '{wait-area-bg}',
  't-wait-pad': '{space.5}',
  't-wait-gap': '{space.3}',
  't-wait-radius': '{radius.r-hair}',
  // AskBar: one question and its one (outlined) button, in the waiting drawing
  't-ask-pad-y': '{space.2}',
  't-ask-pad-r': '{space.2}',
  't-ask-pad-l': '{space.4}',
  't-ask-gap': '{space.3}',
  't-ask-line': '{wait-area}',
  't-ask-bg': '{wait-area-bg}',
  // Totals: the lines, then the total under an ink rule, the figure in the accent
  't-totals-gap': '{space.1}',
  't-totals-line-gap': '{space.4}',
  't-totals-rule': '{line-rule}',
  't-totals-rule-gap': '{space.3}',
  // PhotoThumb: a photo or a picture; dashed while it's still on the phone; ticked when the customer sees it
  't-thumb-bg': '{acc-quiet}',
  't-thumb-line': '{hair}',
  't-thumb-wait': '{dash}',
  't-thumb-on': '{acc-line}',
  't-thumb-radius': '{radius.r-hair}',
  't-thumb-pad-x': '{space.2}',
  't-thumb-pad-y': '{space.1}',
  't-thumb-photo-bg': '{color.bp-surface}',
  't-thumb-row': '{size.art-thumb}',
  't-thumb-tick-inset': '{space.1}',
  't-thumb-tick-size': '{size.check}',
  't-thumb-tick-bg': '{acc-fill}',
  't-thumb-tick-ink': '{acc-on}',
  // PhotoGroup: the visit's photos, three across
  't-photos-gap': '{space.2}',
  't-photos-note-gap': '{space.3}',
  // PhotoViewer: the gallery, its stage large and a row of thumbs under it
  't-viewer-gap': '{space.4}',
  't-viewer-row-gap': '{space.3}',
  't-viewer-count-bg': '{color.bp-surface}',
  't-viewer-count-line': '{hair}',
  't-viewer-count-inset': '{space.4}',
  't-viewer-count-pad-x': '{space.2}',
  't-viewer-inset-x': '{space.6}',
  // one region of the photo marked (the slip's line being checked), on the accent's line
  't-viewer-mark': '{acc-line}',
  // LineTable: what's in an option, in the customer's words with each line's picture, or the tech's
  't-lines-gap': '{space.5}',
  't-lines-pad-y': '{space.3}',
  't-lines-pad-y-tech': '{space.4}',
  't-lines-divider': '{hair}',
  't-lines-art': '{size.art-line}',
  // InOutList: what the price covers and what it doesn't
  't-inout-gap': '{space.7}',
  't-inout-item-gap': '{space.2}',
  't-inout-in': '{acc-fill}',
  't-inout-out': '{color.bp-muted}',
  // PictureBand: a price book card's picture and its words, on the quiet ground
  't-band-bg': '{band-ground}',
  't-band-pad-x': '{space.5}',
  't-band-pad-y': '{space.4}',
  't-band-gap': '{space.5}',
  't-band-radius': '{radius.r-hair}',
  // TickList: lines the customer ticks, the total following
  't-tick-gap': '{space.4}',
  't-tick-pad-y': '{space.2}',
  't-tick-h': '{control-h-touch}',
  't-tick-divider': '{hair}',
  // StepStrip: the visit's steps in call order: now filled in the accent, done hatched
  't-steps-h': '{control-h-touch}',
  't-steps-bg': '{color.bp-surface}',
  't-steps-rule': '{line-rule}',
  't-steps-divider': '{hair}',
  't-steps-ink': '{color.bp-ink}',
  't-steps-done-ink': '{color.bp-muted}',
  't-steps-now-bg': '{acc-fill}',
  't-steps-now-ink': '{acc-on}',
  't-steps-pad-x': '{space.1}',
  // compact, beside the title on the iPad: boxed, each step its own width; the one you're on on the wash
  // (the app's tab above it is the filled one)
  't-steps-compact-border': '{line-rule}',
  't-steps-compact-radius': '{radius.r-hair}',
  't-steps-compact-pad-x': '{space.5}',
  't-steps-compact-now-bg': '{acc-quiet}',
  't-steps-compact-now-ink': '{color.bp-ink}',
  // TabBar: the app's four places, the one you're in marked in the accent above its glyph
  't-tabs-h': '{control-h-big}',
  't-tabs-bg': '{color.bp-surface}',
  't-tabs-rule': '{line-rule}',
  't-tabs-ink': '{color.bp-muted}',
  't-tabs-on': '{acc-fill}',
  't-tabs-mark': '{w.mark}',
  't-tabs-mark-w': '44%',
  't-tabs-pad-y': '{space.2}',
  't-tabs-gap': '{space.1}',
  // a count on a tab (Truck 2)
  't-tabs-count-gap': '{space.1}',
  // at the top on the iPad, as iPadOS draws it: one frame, words only, the one you're in filled
  't-tabs-top-h': '{control-h-touch}',
  't-tabs-top-border': '{line-rule}',
  't-tabs-top-divider': '{hair}',
  't-tabs-top-radius': '{radius.r-hair}',
  't-tabs-top-pad-x': '{space.6}',
  't-tabs-top-on-bg': '{acc-fill}',
  't-tabs-top-on-ink': '{acc-on}',
  // SignatureField: a large dashed pad with nothing to scroll, the name on its line
  't-sign-line': '{dash}',
  't-sign-bg': '{color.bp-surface}',
  't-sign-min-h': 180,
  't-sign-base': '{hair}',
  't-sign-base-x': '{space.6}',
  't-sign-base-y': '{space.8}',
  't-sign-base-pad': '{space.1}',
  't-sign-ink': '{color.bp-ink}',
  't-sign-ink-w': '{w.strong}',
  't-sign-radius': '{radius.r-hair}',
  // ── Patterns ──
  // Screen: the desk, the safe areas, the body's margins
  't-screen-bg': '{color.bp-bg}',
  't-screen-chrome': '{color.bp-surface}',
  't-screen-pad-x': '{space.6}',
  't-screen-pad-top': '{space.6}',
  't-screen-pad-bottom': '{space.9}',
  // ScreenHead: the screen's title on the sheet, under it an ink rule
  't-head-bg': '{color.bp-surface}',
  't-head-rule': '{line-rule}',
  't-head-pad-top': '{space.2}',
  't-head-pad-x': '{space.6}',
  't-head-pad-bottom': '{space.4}',
  't-head-gap': '{space.1}',
  't-head-side-gap': '{space.3}',
  // the iPad's bar over the head: back · the app's tabs · the screen's main action, a hairline under it
  't-head-bar-pad-y': '{space.3}',
  't-head-bar-pad-x': '{space.7}',
  't-head-bar-gap': '{space.6}',
  't-head-bar-rule': '{hair}',
  't-head-ipad-pad-top': '{space.5}',
  't-head-ipad-pad-x': '{space.8}',
  // Section: a rule above, its label under it (DESIGN.md rule 3)
  't-sec-rule': '{line-rule}',
  't-sec-pad-top': '{space.4}',
  't-sec-gap-top': '{space.8}',
  't-sec-gap': '{space.3}',
  // SplitView: the iPad's panes side by side, each scrolling on its own (a rail, a list, the detail)
  't-split-rail-w': '{size.rail}',
  't-split-list-w': '{size.list}',
  't-split-pane-w': '{size.pane}',
  't-split-bg': '{color.bp-surface}',
  't-split-rail-bg': '{color.bp-bg}',
  't-split-divider': '{hair}',
  't-split-pad-x': '{space.7}',
  't-split-pad-top': '{space.6}',
  't-split-pad-bottom': '{space.8}',
  't-split-rail-pad-x': '{space.5}',
  't-split-rail-pad-top': '{space.5}',
  // Dock: the bar at the thumb, above the tab bar
  't-dock-bg': '{color.bp-surface}',
  't-dock-rule': '{line-rule}',
  't-dock-pad-y': '{space.3}',
  't-dock-pad-x': '{space.6}',
  't-dock-gap': '{space.3}',
  't-dock-buy-gap': '{space.6}',
  // BottomSheet: a sheet over the screen, on the panel layer
  't-bsheet-bg': '{color.bp-surface}',
  't-bsheet-rule': '{line-rule}',
  't-bsheet-cast': '{layer-panel}',
  't-bsheet-scrim': '{scrim}',
  't-bsheet-pad-top': '{space.5}',
  't-bsheet-pad-x': '{space.6}',
  't-bsheet-pad-bottom': '{space.8}',
  't-bsheet-gap': '{space.4}',
  // Letterhead: the customer's screens' head, the company's mark and its own colour's rule (ruling 1)
  't-letter-bg': '{color.bp-surface}',
  't-letter-rule': '{letterhead-rule}',
  't-letter-mark': '{brand-mark}',
  't-letter-pad-y': '{space.4}',
  't-letter-pad-x': '{space.6}',
  't-letter-mark-size': '{size.choice}',
  't-letter-mark-w': '{w.strong}',
  't-letter-gap': '{space.3}',
  // pages: how a page lays out its parts (the iPhone's swipe, the sign split, the iPad's three)
  't-page-cards-gap': '{space.5}',
  't-page-lead-gap': '{space.6}',
  't-page-intro-pad-top': '{space.4}',
  't-page-intro-pad-bottom': '{space.3}',
  't-page-swipe-pad-x': '{space.6}',
  't-page-split-gap': '{space.8}',
  't-page-split-pad-x': '{space.6}',
  't-page-split-pad-y': '{space.4}',
  't-page-split-gap-big': '{space.9}',
  't-page-split-pad-x-big': '{space.9}',
  't-page-split-pad-y-big': '{space.8}',
  't-page-ipad-pad-x': '{space.9}',
  't-page-ipad-pad-y': '{space.7}',
  't-page-ipad-gap': '{space.5}',
  // ── Domain ──
  // StopCard: the stop you're on, on the quiet accent ground
  't-stop-bg': '{acc-quiet}',
  't-stop-pad-y': '{space.5}',
  't-stop-pad-x': '{space.6}',
  't-stop-gap': '{space.3}',
  't-stop-radius': '{radius.r-hair}',
  't-stop-acts-gap': '{space.2}',
  // JobHeader: the sync mark on the head's line, its words dashed underneath while work waits to send
  't-jobhead-sync-wait': '{dash}',
  't-jobhead-sync-pad': '{space.1}',
  't-jobhead-sync-slop': '{space.3}',
  // OptionCard: one option as the customer compares it, or the tech's own short card
  't-optcard-line': '{line-rule}',
  't-optcard-rec': '{acc-line}',
  't-optcard-radius': '{radius.r-hair}',
  't-optcard-bg': '{color.bp-surface}',
  't-optcard-gap': '{space.3}',
  't-optcard-pad': '{space.6}',
  't-optcard-pad-bottom': '{space.5}',
  't-optcard-flag-bg': '{acc-fill}',
  't-optcard-flag-pad-y': '{space.2}',
  't-optcard-art-h': '{size.art-card}',
  't-optcard-art-gap': '{space.4}',
  't-optcard-name-line': '{color.bp-rule}',
  't-optcard-facts-rule': '{hair}',
  't-optcard-facts-gap': '{space.4}',
  't-optcard-w': '{size.card-w}',
  't-optcard-compact-pad': '{space.5}',
  't-optcard-compact-gap': '{space.2}',
  't-optcard-head-gap': '{space.5}',
  // OptionPage: one option, open: the gallery, then everything in it
  't-optpage-gap': '{space.6}',
  't-optpage-pad-x': '{space.6}',
  't-optpage-facts-gap': '{space.4}',
  't-optpage-head-gap': '{space.2}',
  // AmountPane: how many of one thing (count, receive, add to a job): its name, the question, − n +, the one button
  't-amount-gap': '{space.5}',
  't-amount-head-gap': '{space.1}',
  't-amount-row-gap': '{space.5}',
} as const;

/** A component token's name in the tech set. */
export type TechToken = keyof typeof TECH_COMPONENTS;

export const tech: TokenSet = {
  ...b,
  name: 'blueprint-tech',
  version: '1.0.0',
  palette: {
    ...b.palette,
    // Fresh water: the accent and its quiet wash, light (600, 50) and dark (300, 900)
    water: {
      '50': FRESH_WATER.light.wash,
      '300': FRESH_WATER.dark.acc,
      '600': FRESH_WATER.light.acc,
      '900': FRESH_WATER.dark.wash,
    },
    // the price book picture's ground on the night sheet: the art worker's blueprint blue
    // (packages/domain pricebook-art-style.ts, ART_RECOLOURS.dark, Amir 24 Oct)
    art: { night: '#143D6B' },
    // a problem in words on the iPhone's desk and on the wash: Blueprint's signal red reads 4.47:1 on the desk
    // (it's held to AA only on the sheet), so the tech app's words take it a step deeper (5.07:1 on the
    // desk, 4.58:1 on the wash). Its marks and rings stay the signal red.
    red: { words: '#C4352A' },
  },
  color: {
    light: {
      ...b.color.light,
      'tc-acc': 'water.600',
      'tc-on-acc': 'fixed.white',
      'tc-wash': 'water.50',
      'tc-art': 'water.50',
      'tc-band': 'water.50',
      'tc-bad': 'red.words',
    },
    dark: {
      ...b.color.dark,
      'tc-acc': 'water.300',
      'tc-on-acc': 'dark.bg',
      'tc-wash': 'water.900',
      'tc-art': 'art.night',
      'tc-band': 'art.night',
      'tc-bad': 'dark.signal',
    },
  },
  contrast: [
    ...(b.contrast ?? []),
    // the accent as words and as a fill, on the sheet, the desk and its own wash (the prototype's art/aa.py)
    { fg: 'tc-acc', bg: 'bp-surface', min: 4.5, why: 'a price, a saving, the tab you are in, on the sheet' },
    { fg: 'tc-acc', bg: 'bp-bg', min: 4.5, why: 'the accent in words on the desk' },
    { fg: 'tc-acc', bg: 'tc-wash', min: 4.5, why: 'the accent in words on its wash (the stop you are at)' },
    { fg: 'tc-on-acc', bg: 'tc-acc', min: 4.5, why: 'the one filled button, the step you are on' },
    { fg: 'bp-ink', bg: 'tc-wash', min: 4.5, why: 'words on the stop you are at, on a photo’s ground' },
    { fg: 'bp-muted', bg: 'tc-wash', min: 4.5, why: 'secondary words on the wash' },
    { fg: 'bp-ink', bg: 'tc-band', min: 4.5, why: 'a price book card’s words beside its picture' },
    { fg: 'tc-acc', bg: 'bp-grid', min: 3, why: 'the accent beside the waiting drawing' },
    { fg: 'tc-bad', bg: 'bp-surface', min: 4.5, why: 'a problem in words on the sheet' },
    { fg: 'tc-bad', bg: 'bp-bg', min: 4.5, why: 'a problem in words on the desk (a screen’s body)' },
    { fg: 'tc-bad', bg: 'tc-wash', min: 4.5, why: 'a problem in words on the stop you are at' },
    { fg: 'tc-bad', bg: 'bp-grid', min: 4.5, why: 'a problem in words in the waiting drawing' },
  ],
  // the iPhone's sizes (the prototype's tokens.css, tc-*): one big control at the thumb (52), its glyph
  // (22), a state mark (14), a checkbox (22), a price book picture in a line (48), on a tech's own option
  // (56), in the gallery's row (64) and on a card (112), a material row's amount (52), a card's width on
  // the phone's swipe (316), a pager's dot (8); and truck stock's (below)
  sizes: {
    ...b.sizes,
    'tap-lg': 52,
    glyph: 22,
    'glyph-sm': 18,
    tick: 16,
    node: 14,
    check: 22,
    dot: 8,
    qty: 52,
    'art-line': 48,
    'art-head': 56,
    'art-thumb': 64,
    'art-card': 112,
    'card-w': 316,
    // truck stock (concept A, 27 Oct): how many's field between − and + (120), a meter (120), and the
    // iPad's split view: a rail at the left thumb (248), a list (300), the detail (380)
    'qty-field': 120,
    meter: 120,
    rail: 248,
    list: 300,
    pane: 380,
  },
  meaning: {
    ...b.meaning,
    // the accent (DESIGN.md rule 2): the one filled thing, its words, its quiet wash, its line and mark
    'acc-fill': '{color.tc-acc}',
    'acc-on': '{color.tc-on-acc}',
    'acc-quiet': '{color.tc-wash}',
    'acc-line': '{w.strong} solid {color.tc-acc}',
    'acc-mark': '{w.mark} solid {color.tc-acc}',
    // a price book picture's ground: the wash in light, the art worker's blueprint blue in dark
    'art-ground': '{color.tc-art}',
    'band-ground': '{color.tc-band}',
    // the one big control at the thumb
    'control-h-big': '{size.tap-lg}',
    // ruling 1: the company's own colour, only for its letterhead's rule and its mark
    'letterhead-rule': '{w.strong} solid {color.bp-brand}',
    'brand-mark': '{color.bp-brand}',
  },
  components: { ...TECH_COMPONENTS },
};
